import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { PNG } from 'pngjs/browser';
import QRCode from 'qrcode';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';

type Props = {
  value: string;
  size?: number;
};

const MODULE_SCALE = 8;
const MARGIN_MODULES = 2;

/**
 * Renders activation QR without DOM canvas (qrcode.toDataURL fails in RN).
 * Builds a PNG from the pure-JS QR matrix via pngjs.
 */
export function ActivationQrCode({ value, size = 240 }: Props) {
  const palette = useAppColors();
  const [dataUri, setDataUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDataUri(null);
    setError(null);
    const token = value.trim();
    if (!token) {
      setError('No activation token to encode.');
      return;
    }

    try {
      const qr = QRCode.create(token, { errorCorrectionLevel: 'M' });
      const moduleCount = qr.modules.size;
      const dim = (moduleCount + MARGIN_MODULES * 2) * MODULE_SCALE;
      const png = new PNG({ width: dim, height: dim });

      for (let y = 0; y < dim; y += 1) {
        for (let x = 0; x < dim; x += 1) {
          const mx = Math.floor(x / MODULE_SCALE) - MARGIN_MODULES;
          const my = Math.floor(y / MODULE_SCALE) - MARGIN_MODULES;
          const dark =
            mx >= 0 &&
            my >= 0 &&
            mx < moduleCount &&
            my < moduleCount &&
            Boolean(qr.modules.get(my, mx));
          const idx = (dim * y + x) << 2;
          const tone = dark ? 17 : 255;
          png.data[idx] = tone;
          png.data[idx + 1] = tone;
          png.data[idx + 2] = tone;
          png.data[idx + 3] = 255;
        }
      }

      const buffer = PNG.sync.write(png);
      const uri = `data:image/png;base64,${buffer.toString('base64')}`;
      if (!cancelled) setDataUri(uri);
    } catch {
      if (!cancelled) setError('Unable to render activation QR code.');
    }

    return () => {
      cancelled = true;
    };
  }, [value]);

  if (error) {
    return (
      <View style={[styles.box, { borderColor: palette.border }]}>
        <Text style={[styles.error, { color: palette.error }]}>{error}</Text>
      </View>
    );
  }

  if (!dataUri) {
    return (
      <View style={[styles.box, { borderColor: palette.border, height: size }]}>
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.box, { borderColor: palette.border }]}>
      <Image
        accessibilityLabel="Kiosk activation QR code"
        source={{ uri: dataUri }}
        style={{ width: size, height: size }}
        resizeMode="contain"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.md,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    ...typography.body,
    textAlign: 'center',
  },
});
